import React, { Dispatch, SetStateAction, useRef } from 'react'

import { Button, Grid, Typography } from '@mui/material'
import { Box } from '@mui/system'

import AgreedTermsCheckbox from 'components/AgreedTermsCheckbox/AgreedTermsCheckbox'
import { RichTextField } from 'components/Form'
import IdeaFlameMotivator, {
	useRichTextToIdeas,
} from 'components/IdeaFlameMotivator/IdeaFlameMotivator'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section3.styles'

export const TEST_ID = 'teeter-totter-section3'

export type Props = {
	authorIdeation: string
	gameId?: string
	isLoading: boolean
	onSubmitIdeaClick: () => void
	onViewIdeasClick: () => void
	setAuthorIdeation: (val: string) => void
	setTermsAgreed: Dispatch<SetStateAction<boolean>>
	submitIdeasDisabled: () => boolean
	termsAgreed: boolean
}

const Section3 = (props: Props) => {
	const richTextFieldRef = useRef(null)
	const richTextToIdeas = useRichTextToIdeas()

	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.header }}
				data-testid={`${TEST_ID}__heading`}
			>
				IDEATION
			</Typography>
			<Box
				display={'flex'}
				sx={{
					'.ProseMirror': {
						border: '1.5px solid var(--color-gray-4)',
					},
					'.ProseMirror-focused': {
						border: '2.5px solid var(--color-gray-4)',
					},
				}}
			>
				<Box marginRight={'3rem'} sx={styles.sideBar} />
				<Box
					display="flex"
					flexDirection="column"
					gap="2rem"
					flexGrow="1"
				>
					<Typography
						sx={{ ...commonStyles.header, ...styles.header }}
						data-testid={`${TEST_ID}__ideation-text`}
					>
						Document any ideas inspired by the facilitation
						technique to address your innovation topic.
					</Typography>
					<Grid
						item
						xs={12}
						sm={10}
						sx={{
							minHeight: { xs: '15rem', sm: '20rem' },
						}}
						data-testid={`${TEST_ID}__ideation`}
					>
						<RichTextField
							ref={richTextFieldRef}
							onChange={props.setAuthorIdeation}
							value={props.authorIdeation}
							placeholderText="Brainstorm your ideas here!"
							sx={{
								background: '#FFDDE7',
								borderLeft: '1.5px solid rgba(0,0,0,0.23)',
								borderRight: '1.5px solid rgba(0,0,0,0.23)',
								borderTop: '1.5px solid rgba(0,0,0,0.23)',
							}}
							iconColor="#45657B"
						/>
					</Grid>
					<AgreedTermsCheckbox
						setTermsAgreed={props.setTermsAgreed}
						termsAgreed={props.termsAgreed}
						gameId={props.gameId}
					/>
					<Box>
						<IdeaFlameMotivator
							ideaCount={richTextToIdeas(props.authorIdeation)}
							variant="TEETER_TOTTER"
						/>
					</Box>
					<Box
						display="flex"
						sx={{
							flexDirection: {
								xs: 'column-reverse',
								sm: 'row',
							},
						}}
						gap="2rem"
					>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__submit-idea-button`}
							onClick={props.onSubmitIdeaClick}
							disabled={
								props.submitIdeasDisabled() || props.isLoading
							}
						>
							{props.isLoading ? (
								<LoadingSpinner inline />
							) : (
								<Typography>SUBMIT IDEAS</Typography>
							)}
						</Button>
						<Button
							disabled={props.isLoading}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__view-submit-ideas-button`}
							onClick={props.onViewIdeasClick}
						>
							{props.isLoading ? (
								<LoadingSpinner inline />
							) : (
								<Typography>
									VIEW ALL SUBMITTED IDEAS
								</Typography>
							)}
						</Button>
					</Box>
					<Typography
						color="black"
						sx={{ opacity: 0.6 }}
						data-testid={`${TEST_ID}__caution-text`}
					>
						Don’t worry, you’re the only one who can see your
						submitted ideas!
					</Typography>
				</Box>
			</Box>
		</>
	)
}

export default Section3
